import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../utils/axios';
import toast from 'react-hot-toast';
import PaymentCollectionModal from '../../components/admin/payments/PaymentCollectionModal';
import OpeningBalanceModal from '../../components/admin/payments/OpeningBalanceModal';

// ============================================
// Component: AdminCompaniesDebt
// Purpose: Admin view of all companies' debt overview
// Enhanced Features:
// - View ALL companies (not just agent's companies)
// - Advanced filtering (by agent, status, credit frame, debt range)
// - Statistics dashboard
// - Export functionality
// - Quick payment collection
// ============================================

const AdminCompaniesDebt = () => {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [summary, setSummary] = useState({});
  const [originalSummary, setOriginalSummary] = useState({}); // Summary without search filter
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false); // Loading only for table
  const [filterStatus, setFilterStatus] = useState('all'); // all, has_debt, clear, overpaid, over_limit
  const [filterAgent, setFilterAgent] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showCollectionModal, setShowCollectionModal] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [showOpeningBalanceModal, setShowOpeningBalanceModal] = useState(false);
  const [selectedCompanyForBalance, setSelectedCompanyForBalance] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, pages: 1, limit: 50 });
  const [sortBy, setSortBy] = useState('debt_desc');

  // Debounce search
  useEffect(() => {
    const delaySearch = setTimeout(() => {
      setCurrentPage(1); // Reset to page 1 on new search
      fetchCompaniesDebt();
    }, 500); // Wait 500ms after user stops typing

    return () => clearTimeout(delaySearch);
  }, [searchQuery]);

  useEffect(() => {
    fetchCompaniesDebt();
  }, [filterAgent, filterStatus, currentPage, sortBy]);

  useEffect(() => {
    fetchAgents();
  }, []);

  const fetchCompaniesDebt = async () => {
    try {
      const isSearching = searchQuery.trim() !== '';

      // If searching, only show table loading. Otherwise show full page loading
      if (isSearching) {
        setTableLoading(true);
      } else {
        setLoading(true);
      }

      const params = {
        page: currentPage,
        limit: 50,
        sort_by: sortBy
      };

      if (filterAgent) params.agent_id = filterAgent;
      if (filterStatus !== 'all') params.status = filterStatus;
      if (isSearching) params.search = searchQuery.trim();

      const response = await axiosInstance.get('/api/admin/payments/companies-overview', { params });

      // Always update companies and pagination
      setCompanies(response.data.companies);
      setPagination(response.data.pagination);

      // Only update summary if not searching (keep original stats visible)
      if (!isSearching) {
        setSummary(response.data.summary);
        setOriginalSummary(response.data.summary);
      }
    } catch (error) {
      toast.error('שגיאה בטעינת נתוני חובות');
    } finally {
      setLoading(false);
      setTableLoading(false);
    }
  };

  const fetchAgents = async () => {
    try {
      const response = await axiosInstance.get('/api/admin/field-agents');
      setAgents(response.data.field_agents || []);
    } catch (error) {
      console.error('Error fetching agents:', error);
    }
  };

  const handleCollectPayment = (company) => {
    setSelectedCompany({
      id: company.company_id,
      name: company.company_name,
      current_balance: company.current_balance,
      available_credit: company.available_credit
    });
    setShowCollectionModal(true);
  };

  const handlePaymentSuccess = () => {
    fetchCompaniesDebt();
    setShowCollectionModal(false);
    setSelectedCompany(null);
  };

  const handleOpeningBalanceClick = (company) => {
    setSelectedCompanyForBalance({
      company_id: company.company_id,
      company_name: company.company_name
    });
    setShowOpeningBalanceModal(true);
  };

  const handleOpeningBalanceSuccess = () => {
    fetchCompaniesDebt();
    setShowOpeningBalanceModal(false);
    setSelectedCompanyForBalance(null);
  };

  // Search is now handled server-side
  const filteredCompanies = companies;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('he-IL', {
      style: 'currency',
      currency: 'ILS'
    }).format(amount);
  };

  const getStatusBadge = (company) => {
    if ((company.used_credit || 0) > (company.credit_limit || 0)) {
      return <span className="px-2 py-1 text-xs rounded-full bg-red-200 text-red-800 font-bold">חריגת מסגרת</span>;
    }
    if ((company.current_balance || 0) > 0) {
      return <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-700">חוב פתוח</span>;
    }
    if ((company.current_balance || 0) < 0) {
      return <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-700">עודף תשלום</span>;
    }
    return <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-700">מאוזן</span>;
  };

  const getCreditUtilizationColor = (utilization) => {
    if (utilization >= 100) return 'text-red-700 font-bold';
    if (utilization >= 90) return 'text-red-600';
    if (utilization >= 70) return 'text-yellow-600';
    return 'text-green-600';
  };

  const formatUtilization = (utilization) => {
    if (utilization === undefined || utilization === null || isNaN(utilization)) {
      return '0%';
    }
    if (utilization >= 100) {
      return 'חריגה';
    }
    return `${utilization}%`;
  };

  const getDaysSincePaymentColor = (days) => {
    if (days === null) return 'text-gray-500';
    if (days > 60) return 'text-red-600 font-bold';
    if (days > 30) return 'text-yellow-600';
    return 'text-green-600';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="w-full mx-auto px-2 sm:px-4 lg:px-6 py-6" dir="rtl">
      {/* Header */}
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-gray-900 mb-1">חובות חברות</h1>
        <p className="text-sm text-gray-600">מעקב אחר חובות וגבייה מכל החברות במערכת</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2 md:gap-3 mb-4">
        <div className="bg-white rounded-lg shadow p-3">
          <div className="text-xs text-gray-600 mb-1">סה"כ חברות</div>
          <div className="text-xl font-bold text-gray-900">{pagination.total || 0}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-3">
          <div className="text-xs text-gray-600 mb-1">חברות עם חוב</div>
          <div className="text-xl font-bold text-red-600">
            {summary.companies_with_debt || 0}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-3">
          <div className="text-xs text-gray-600 mb-1">סה"כ חובות</div>
          <div className="text-xl font-bold text-red-600">
            {formatCurrency(summary.total_debt || 0)}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-3">
          <div className="text-xs text-gray-600 mb-1">סה"כ גבייה</div>
          <div className="text-lg font-bold text-green-600">
            {formatCurrency(summary.total_collected || 0)}
          </div>
          <div className="text-xs text-gray-500 mt-1" title="שולם בפועל (מזומן/צ'ק/העברה)">
            בפועל: {formatCurrency(summary.total_real_collected || 0)}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-3">
          <div className="text-xs text-gray-600 mb-1">סה"כ זיכויים</div>
          <div className="text-lg font-bold text-orange-600">
            {formatCurrency(summary.total_refunds || 0)}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-3">
          <div className="text-xs text-gray-600 mb-1">חריגות מסגרת</div>
          <div className="text-xl font-bold text-red-700">
            {summary.companies_over_limit || 0}
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow p-3 mb-4">
        {/* Search Bar */}
        <div className="mb-3">
          <div className="relative">
            <input
              type="text"
              placeholder="חיפוש לפי שם חברה או ח.פ..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pr-10 pl-4 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
            <div className="absolute right-3 top-2.5 text-gray-400">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute left-3 top-2.5 text-gray-400 hover:text-gray-600"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Agent Filter */}
        <div className="mb-3">
          <label className="block text-xs font-medium text-gray-700 mb-1">סינון לפי סוכן:</label>
          <select
            value={filterAgent}
            onChange={(e) => setFilterAgent(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500"
          >
            <option value="">כל הסוכנים</option>
            {agents.map(agent => (
              <option key={agent.id} value={agent.user_id}>
                {agent.user?.first_name} {agent.user?.last_name}
              </option>
            ))}
          </select>
        </div>

        {/* Status Filters */}
        <div className="flex gap-2 overflow-x-auto pb-2">
          <button
            onClick={() => { setFilterStatus('all'); setCurrentPage(1); }}
            className={`px-3 py-1.5 text-sm rounded-md transition-colors whitespace-nowrap ${filterStatus === 'all'
              ? 'bg-primary-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
          >
            הכל ({pagination.total || 0})
          </button>
          <button
            onClick={() => { setFilterStatus('has_debt'); setCurrentPage(1); }}
            className={`px-3 py-1.5 text-sm rounded-md transition-colors whitespace-nowrap ${filterStatus === 'has_debt'
              ? 'bg-primary-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
          >
            עם חוב
          </button>
          <button
            onClick={() => { setFilterStatus('over_limit'); setCurrentPage(1); }}
            className={`px-3 py-1.5 text-sm rounded-md transition-colors whitespace-nowrap ${filterStatus === 'over_limit'
              ? 'bg-primary-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
          >
            חריגת מסגרת
          </button>
          <button
            onClick={() => { setFilterStatus('clear'); setCurrentPage(1); }}
            className={`px-3 py-1.5 text-sm rounded-md transition-colors whitespace-nowrap ${filterStatus === 'clear'
              ? 'bg-primary-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
          >
            מאוזנות
          </button>
          <button
            onClick={() => { setFilterStatus('overpaid'); setCurrentPage(1); }}
            className={`px-3 py-1.5 text-sm rounded-md transition-colors whitespace-nowrap ${filterStatus === 'overpaid'
              ? 'bg-primary-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
          >
            עודף תשלום
          </button>
        </div>

        {/* Results count */}
        {searchQuery && (
          <div className="mt-2 text-xs text-gray-600">
            נמצאו {filteredCompanies.length} תוצאות
          </div>
        )}
      </div>

      {/* Companies Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {/* Search indicator */}
        {searchQuery.trim() && (
          <div className="px-4 py-2 bg-blue-50 border-b border-blue-100 flex items-center gap-2">
            <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <span className="text-sm text-blue-700">
              מחפש: <span className="font-semibold">{searchQuery}</span>
            </span>
            <button
              onClick={() => setSearchQuery('')}
              className="mr-auto text-blue-600 hover:text-blue-800 text-sm font-medium"
            >
              נקה חיפוש
            </button>
          </div>
        )}

        <div className="overflow-x-auto relative">
          {/* Table loading overlay */}
          {tableLoading && (
            <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
              <div className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                <span className="text-sm text-gray-600">מחפש...</span>
              </div>
            </div>
          )}

          <table className="w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500">
                  <button
                    onClick={() => setSortBy(sortBy === 'name_asc' ? 'name_desc' : 'name_asc')}
                    className="flex items-center gap-1 hover:text-gray-700"
                  >
                    חברה
                    {sortBy.startsWith('name') && (
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d={sortBy === 'name_asc' ? 'M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z' : 'M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z'} clipRule="evenodd" />
                      </svg>
                    )}
                  </button>
                </th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500">
                  סוכן
                </th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500">
                  מסגרת
                </th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500">
                  <button
                    onClick={() => setSortBy(sortBy === 'debt_asc' ? 'debt_desc' : 'debt_asc')}
                    className="flex items-center gap-1 hover:text-gray-700"
                  >
                    חוב נוכחי
                    {sortBy.startsWith('debt') && (
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d={sortBy === 'debt_asc' ? 'M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z' : 'M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z'} clipRule="evenodd" />
                      </svg>
                    )}
                  </button>
                </th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500">
                  ניצול
                </th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500">
                  תשלום אחרון
                </th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500">
                  סטטוס
                </th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500">
                  פעולות
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredCompanies.map((company) => (
                <tr key={company.company_id} className="hover:bg-gray-50">
                  <td className="px-2 py-2">
                    <div className="text-sm font-medium text-gray-900 truncate max-w-[150px]" title={company.company_name}>
                      {company.company_name}
                    </div>
                    <div className="text-xs text-gray-500">{company.business_number}</div>
                  </td>
                  <td className="px-2 py-2 text-xs text-gray-700">
                    {company.agent?.name || '-'}
                  </td>
                  <td className="px-2 py-2 text-sm text-gray-900 whitespace-nowrap">
                    {formatCurrency(company.credit_limit || 0)}
                  </td>
                  <td className="px-2 py-2 whitespace-nowrap">
                    <span className={`text-sm font-bold ${(company.current_balance || 0) > 0 ? 'text-red-600' : (company.current_balance || 0) < 0 ? 'text-blue-600' : 'text-green-600'}`}>
                      {formatCurrency(company.current_balance || 0)}
                    </span>
                  </td>
                  <td className="px-2 py-2 whitespace-nowrap">
                    <span className={`text-sm font-medium ${getCreditUtilizationColor(company.utilization)}`}>
                      {formatUtilization(company.utilization)}
                    </span>
                  </td>
                  <td className="px-2 py-2 whitespace-nowrap">
                    <span className={`text-xs ${getDaysSincePaymentColor(company.days_since_payment)}`}>
                      {company.days_since_payment !== null ? `${company.days_since_payment} ימים` : 'אין'}
                    </span>
                  </td>
                  <td className="px-2 py-2 whitespace-nowrap">
                    {getStatusBadge(company)}
                  </td>
                  <td className="px-2 py-2 whitespace-nowrap text-xs space-x-2 space-x-reverse">
                    <button
                      onClick={() => navigate(`/admin/companies-debt/${company.company_id}`)}
                      className="text-primary-600 hover:text-primary-800 font-medium"
                    >
                      פרטים
                    </button>
                    {' | '}
                    <button
                      onClick={() => handleCollectPayment(company)}
                      className="text-green-600 hover:text-green-800 font-medium"
                    >
                      גבה
                    </button>
                    {' | '}
                    <button
                      onClick={() => handleOpeningBalanceClick(company)}
                      className="text-blue-600 hover:text-blue-800 font-medium"
                      title="עדכן חוב התחלתי"
                    >
                      חוב פתיחה
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredCompanies.length === 0 && (
          <div className="text-center py-12">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">לא נמצאו תוצאות</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchQuery
                ? `לא נמצאו חברות עם "${searchQuery}"`
                : 'לא נמצאו חברות בסינון זה'}
            </p>
          </div>
        )}

        {/* Pagination Controls */}
        {pagination.pages > 1 && (
          <div className="px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                הקודם
              </button>
              <button
                onClick={() => setCurrentPage(Math.min(pagination.pages, currentPage + 1))}
                disabled={currentPage === pagination.pages}
                className="mr-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                הבא
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  מציג{' '}
                  <span className="font-medium">{(currentPage - 1) * pagination.limit + 1}</span>
                  {' '}עד{' '}
                  <span className="font-medium">
                    {Math.min(currentPage * pagination.limit, pagination.total)}
                  </span>
                  {' '}מתוך{' '}
                  <span className="font-medium">{pagination.total}</span>
                  {' '}תוצאות
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">הקודם</span>
                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </button>

                  {/* Page numbers */}
                  {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                    let pageNum;
                    if (pagination.pages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= pagination.pages - 2) {
                      pageNum = pagination.pages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${currentPage === pageNum
                          ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}

                  <button
                    onClick={() => setCurrentPage(Math.min(pagination.pages, currentPage + 1))}
                    disabled={currentPage === pagination.pages}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">הבא</span>
                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Payment Collection Modal */}
      {selectedCompany && (
        <PaymentCollectionModal
          isOpen={showCollectionModal}
          onClose={() => {
            setShowCollectionModal(false);
            setSelectedCompany(null);
          }}
          preselectedCompany={selectedCompany}
          onSuccess={handlePaymentSuccess}
        />
      )}

      {/* Opening Balance Modal */}
      {selectedCompanyForBalance && (
        <OpeningBalanceModal
          isOpen={showOpeningBalanceModal}
          onClose={() => {
            setShowOpeningBalanceModal(false);
            setSelectedCompanyForBalance(null);
          }}
          company={selectedCompanyForBalance}
          onSuccess={handleOpeningBalanceSuccess}
        />
      )}
    </div>
  );
};

export default AdminCompaniesDebt;
